// ─── MapRenderer ─────────────────────────────────────────────────────────
// Owns every Leaflet layer on the map. Data in → layers out.
//
// Responsibilities:
//   1. Custom panes with stable z-ordering
//   2. Layer groups (main, poi, connections)
//   3. Render methods for each visual concern:
//      1. renderTripLegs        saved trip polylines
//      2. renderTripDayBadges   "Day N" pills on trip line
//      3. renderTripStops       markers for saved trip stops
//      4. renderInactiveRoutes  dashed polylines for non-selected search routes
//      5. renderActiveRoute     dashed preview polylines + selected route markers
//      6. renderPOIs            POI dot markers (explore + search)
//
// MapView's only job is to call these methods from useEffect.
// ─────────────────────────────────────────────────────────────────────────

import L from "leaflet";
import {
  City,
  CityWithTimes,
  TrainRoute,
  TripLeg,
  StopActions,
  ExploreFilters,
  DEFAULT_EXPLORE_FILTERS,
} from "../data/types";
import {
  DAY_COLORS,
  NEUTRAL,
  FONT_STACK,
  iconElement,
} from "../data/design-tokens";
import { getCityById } from "./cityStore";
import { type POI, POI_CONFIG } from "./poiService";
import type { POIType } from "../data/database.types";
import { calendarDaysBetween, journeyDaySegments, journeyStationDay, type JourneyDaySegment } from "./journeyTime";

import {
  escapeHtml,
  createRouteMarker,
  createDayBadge,
  createPinnedStopMarker,
  createPOIDot,
} from "./mapMarkerFactories";

// ─── Module-level constant: POI SVG symbols (built once) ────────────

let _poiSymbolMap: Record<string, string> | null = null;
function getPoiSymbolMap(): Record<string, string> {
  _poiSymbolMap ??= {
    "heritage-landmark": iconElement("heritage-landmark", "svg"),
    "hidden-gem": iconElement("hidden-gem", "svg"),
    castle: iconElement("castle", "svg"),
    "natural-landmark": iconElement("natural-landmark", "svg"),
  };
  return _poiSymbolMap;
}

// ─── Callback shapes (thin delegation back to React) ────────────────────

export interface MarkerCallbacks {
  onSelectStop: (station: City) => void;
  onSelectStopWithPosition?: (station: City, pos: { x: number; y: number }) => void;
  onSelectRoute: (index: number) => void;
  onAddRouteToTrip?: (route: TrainRoute) => void;
  onSearchConnection?: (from: City, to: City) => void;
  onSetHoveredPOI?: (poi: POI, pos: { x: number; y: number }) => void;
  onClearHoveredPOI?: () => void;
}

// ─── Pane & group types ─────────────────────────────────────────────────

export type PaneKey = "tripPane" | "routePane" | "connectionPane";
export type GroupKey = "main" | "poi" | "connections";

const PANE_Z: Record<PaneKey, number> = {
  tripPane: 370,
  routePane: 380,
  connectionPane: 415,
};

// ─── Helper: resolve station IDs → [lat,lng] coords ────────────────────

function resolveCoords(ids: string[]): [number, number][] {
  const coords: [number, number][] = [];
  for (const id of ids) {
    const s = getCityById(id);
    if (s && Number.isFinite(s.latitude) && Number.isFinite(s.longitude)) coords.push([s.latitude, s.longitude]);
  }
  return coords;
}

// ─── Helper: fire click with pixel position ─────────────────────────────

function bindStopClick(
  marker: L.Marker,
  station: City,
  map: L.Map,
  cb: MarkerCallbacks
): void {
  marker.on("click", () => {
    if (cb.onSelectStopWithPosition) {
      const pos = map.latLngToContainerPoint([station.latitude, station.longitude]);
      cb.onSelectStopWithPosition(station, { x: pos.x, y: pos.y });
    } else {
      cb.onSelectStop(station);
    }
  });
}

type ActiveStops = { ids: Set<string>; cities: Set<string> };

function isActiveStop(station: City, active: ActiveStops): boolean {
  return active.ids.has(station.id) || active.cities.has(station.city.toLowerCase());
}

function hasCoordinates(station: City | undefined): station is City {
  return !!station && Number.isFinite(station.latitude) && Number.isFinite(station.longitude);
}

function tripStopLabel(index: number, count: number): string {
  if (index === 0) return "Trip start";
  if (index === count - 1) return "Trip end";
  return `Stop ${index + 1}`;
}

function stationTooltip(station: City, time: string, color: string, day: number, showDay: boolean): string {
  let tooltip = `<div style="font-size:12px;line-height:1.3"><span style="font-weight:600">${escapeHtml(station.city)}</span>`;
  const poiSvg = station.poiType ? iconElement(station.poiType, "svg", true) : "";
  if (poiSvg) tooltip += `<span style="margin-left:4px;vertical-align:middle;">${poiSvg}</span>`;
  if (time) {
    const timeLine = showDay ? `Day ${day} ${time}` : time;
    tooltip += `<span style="display:block;font-size:10px;color:${color};font-weight:600">${escapeHtml(timeLine)}</span>`;
  }
  return `${tooltip}</div>`;
}

// ─── Helper: build "Day N HH:MM" label ──────────────────────────────────

function makeTimeLabel(time: string | undefined, day: number | undefined, showDay: boolean): string {
  if (!time) return "";
  return showDay && day ? `Day ${day} ${time}` : time;
}

interface StationTimes { arrival?: string; departure?: string; arrivalDay?: number; departureDay?: number }

function pinnedTimeLabel(times: StationTimes | undefined, overnight: boolean, showDay: boolean): string {
  if (!times) return "";
  if (!overnight) return makeTimeLabel(times.arrival || times.departure, times.arrivalDay ?? times.departureDay, showDay);
  const lines: string[] = [];
  if (times.arrival) lines.push(`Arr ${makeTimeLabel(times.arrival, times.arrivalDay, true)}`);
  if (times.departure) lines.push(`Dep ${makeTimeLabel(times.departure, times.departureDay, true)}`);
  return lines.join("\n");
}

function savedStationTimes(legs: TripLeg[], days: Map<string, { day: number; color: string }> | undefined): Map<string, StationTimes> {
  const times = new Map<string, StationTimes>();
  for (const leg of legs) {
    const journeyDays = leg.departureDate && leg.arrivalDate ? Math.max(0, calendarDaysBetween(leg.departureDate, leg.arrivalDate)) : 0;
    times.set(leg.fromStationId, { ...times.get(leg.fromStationId), departure: leg.departureTime,
      departureDay: leg.dayNumber ?? days?.get(leg.fromStationId)?.day ?? 1 });
    times.set(leg.toStationId, { ...times.get(leg.toStationId), arrival: leg.arrivalTime,
      arrivalDay: days?.get(leg.toStationId)?.day ?? (leg.dayNumber ?? 1) + journeyDays });
  }
  return times;
}

function tripLinePresentation(leg: TripLeg | undefined, color: string, isFallback: boolean) {
  let style: L.PolylineOptions = { color, weight: 5, opacity: 0.65, lineCap: "round", lineJoin: "round" };
  let connectionLabel = leg?.isGhostLeg ? "Automatic connection" : "Saved itinerary";
  if (leg?.requiresSelection) {
    style = { color: "#6b7280", weight: 3, opacity: 0.75, dashArray: "3 8", lineCap: "round", lineJoin: "round" };
    connectionLabel = "Choose your onward train";
  } else if (leg?.ghostLoading) {
    style = { color, weight: 3, opacity: 0.35, dashArray: "8 12", lineCap: "round", lineJoin: "round", className: "ghost-leg-pulse" };
    connectionLabel = "Finding a connection";
  } else if (leg?.ghostError || leg?.ghostThrottled || isFallback) {
    style = { color, weight: 2, opacity: 0.12, dashArray: "4 12", lineCap: "round", lineJoin: "round" };
    connectionLabel = leg?.ghostThrottled ? "Waiting to retry" : "No train selected";
  }
  return { style, connectionLabel };
}

function tripLegGeometry(leg: TripLeg | undefined, from: City, to: City, day: number, color: string) {
  let coordinates = leg?.routeCoords ?? [];
  if (coordinates.length < 2 && leg?.stationData?.length) coordinates = leg.stationData.filter(hasCoordinates).map(station => [station.latitude, station.longitude]);
  if (coordinates.length < 2 && leg?.stations.length) coordinates = resolveCoords(leg.stations);
  const isFallback = coordinates.length < 2;
  if (isFallback) coordinates = [[from.latitude, from.longitude], [to.latitude, to.longitude]];
  const segments = leg ? journeyDaySegments({ ...leg, routeCoords: coordinates, stationData: leg.stationData?.length ? leg.stationData : [from, to] }, day, color)
    : [{ day, color, stationIds: [from.id, to.id], coordinates }];
  return { segments, coordinates, isFallback };
}

function tripStationPresentation(leg: TripLeg, station: CityWithTimes, startDay: number) {
  const day = journeyStationDay(leg, station, startDay);
  const color = day === startDay ? leg.dayColor ?? DAY_COLORS[(startDay - 1) % DAY_COLORS.length] : DAY_COLORS[(day - 1) % DAY_COLORS.length];
  return { day, color };
}

function selectMapPOIs(
  viewMode: "search" | "explore", allPOIs: POI[] = [], routePOIs: POI[] = [], tripPOIs: POI[] = [],
  filters: ExploreFilters = DEFAULT_EXPLORE_FILTERS,
): POI[] {
  if (viewMode === "explore") {
    return allPOIs.filter(poi => {
      if (!hasCoordinates(poi)) return false;
      if (poi.poiType === "heritage-landmark" && !filters.showLandmarks) return false;
      if (poi.poiType === "hidden-gem" && !filters.showHiddenGems) return false;
      if (poi.poiType === "castle" && !filters.showCastles) return false;
      if (poi.poiType === "natural-landmark" && !filters.showNaturalLandmarks) return false;
      return true;
    });
  }
  const seen = new Set<string>();
  const merged: POI[] = [];
  for (const poi of [...routePOIs, ...tripPOIs]) {
    if (!hasCoordinates(poi)) continue;
    const key = poi.id ?? `${poi.latitude},${poi.longitude}`;
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(poi);
  }
  return merged;
}

// ═════════════════════════════════════════════════════════════════════════

export class MapRenderer {
  private readonly map: L.Map;
  private readonly groups: Record<GroupKey, L.LayerGroup>;

  constructor(map: L.Map) {
    this.map = map;
    for (const [pane, z] of Object.entries(PANE_Z)) {
      map.createPane(pane);
      map.getPane(pane)!.style.zIndex = String(z);
    }
    this.groups = {
      main: L.layerGroup().addTo(map),
      poi: L.layerGroup().addTo(map),
      connections: L.layerGroup().addTo(map),
    };
  }

  // ── Group accessors ──────────────────────────────────────────────

  getGroup(key: GroupKey): L.LayerGroup { return this.groups[key]; }
  clearGroup(key: GroupKey): void {
    if (key === "main") this.map.closePopup();
    this.groups[key].clearLayers();
  }
  clearAll(): void { this.map.closePopup(); for (const g of Object.values(this.groups)) g.clearLayers(); }

  // ── Low-level polyline helpers (pane-assigned) ───────────────────

  private addTripPolyline(coords: L.LatLngExpression[], opts?: Omit<L.PolylineOptions, "pane">): L.Polyline {
    const line = L.polyline(coords, { ...opts, pane: "tripPane" });
    this.groups.main.addLayer(line);
    return line;
  }

  private addRoutePolyline(coords: L.LatLngExpression[], opts?: Omit<L.PolylineOptions, "pane">): L.Polyline {
    const line = L.polyline(coords, { ...opts, pane: "routePane" });
    const hitTarget = L.polyline(coords, { pane: "routePane", color: opts?.color, weight: 20, opacity: 0, bubblingMouseEvents: false });
    hitTarget.on("click", event => line.fire("click", event));
    this.groups.main.addLayer(hitTarget);
    this.groups.main.addLayer(line);
    return line;
  }

  addConnectionLine(coords: L.LatLngExpression[], opts?: Omit<L.PolylineOptions, "pane">): L.Polyline {
    const line = L.polyline(coords, { ...opts, pane: "connectionPane" });
    this.groups.connections.addLayer(line);
    return line;
  }

  addMarker(latlng: L.LatLngExpression, opts?: L.MarkerOptions, group: GroupKey = "main"): L.Marker {
    const marker = L.marker(latlng, opts);
    this.groups[group].addLayer(marker);
    return marker;
  }

  addLayer(layer: L.Layer, group: GroupKey = "main"): void {
    this.groups[group].addLayer(layer);
  }

  // ═══════════════════════════════════════════════════════════════════
  // HIGH-LEVEL RENDER METHODS
  // Each is a self-contained black box: data in → layers added.
  // Call clearGroup("main") before the render cycle, not inside.
  // ═══════════════════════════════════════════════════════════════════

  // ── 1. Trip leg polylines ─────────────────────────────────────────

  renderTripLegs(
    tripStops: City[],
    tripLegs: TripLeg[],
    tripStationDayMap?: Map<string, { color: string; day: number }>,
    activeRoute?: TrainRoute,
    cb?: MarkerCallbacks,
  ): void {
    if (tripStops.length < 2) return;

    const legMap = new Map<string, TripLeg>();
    for (const leg of tripLegs) legMap.set(`${leg.fromStationId}→${leg.toStationId}`, leg);

    for (let i = 0; i < tripStops.length - 1; i++) {
      const from = tripStops[i];
      const to = tripStops[i + 1];
      const leg = legMap.get(`${from.id}→${to.id}`);
      if (activeRoute && leg?.routeId === activeRoute.id && !leg.requiresSelection) continue;

      const color = leg?.dayColor ?? tripStationDayMap?.get(from.id)?.color ?? DAY_COLORS[0];
      const day = leg?.dayNumber ?? tripStationDayMap?.get(from.id)?.day ?? 1;

      const { segments, coordinates, isFallback } = tripLegGeometry(leg, from, to, day, color);
      for (const segment of segments) {
        const points = segment.coordinates ?? coordinates;
        if (points.length < 2) continue;
        this.renderTripSegment(points, segment, leg, from, to, isFallback, cb);
      }
    }
  }

  private renderTripSegment(points: [number, number][], segment: JourneyDaySegment, leg: TripLeg | undefined, from: City, to: City, isFallback: boolean, cb?: MarkerCallbacks): void {
    const { style, connectionLabel } = tripLinePresentation(leg, segment.color, isFallback);
    const polyline = this.addTripPolyline(points, style);
    const timeStr = leg?.departureTime && leg?.arrivalTime
      ? `<span style="display:block;font-size:11px;color:${NEUTRAL.subtitle};font-weight:500">${escapeHtml(leg.departureTime)} → ${escapeHtml(leg.arrivalTime)} · ${escapeHtml(leg.duration)}</span>` : "";
    polyline.bindTooltip(
        `<div style="font-size:12px;line-height:1.4">
          <span style="font-weight:500;color:${segment.color}">Day ${segment.day}</span>
          <span style="display:block;font-size:11px;color:${NEUTRAL.tertiary}">${escapeHtml(from.city)} → ${escapeHtml(to.city)}</span>
          <span style="display:block;font-size:11px;color:${NEUTRAL.tertiary}">${connectionLabel}</span>
          ${timeStr}
        </div>`,
        { sticky: true }
      );
    if (leg?.requiresSelection) {
      const target = this.addTripPolyline(points, { weight: 20, opacity: 0, bubblingMouseEvents: false });
      target.bindTooltip(polyline.getTooltip()!.getContent()!, { sticky: true });
      target.on("click", () => cb?.onSearchConnection?.(from, to));
    }
  }

  // ── 2. Trip day badges ────────────────────────────────────────────

  private addTripDayBadge(station: City | undefined, day: number, color: string, active: ActiveStops): void {
    if (!station || isActiveStop(station, active)) return;
    this.groups.main.addLayer(L.marker([station.latitude, station.longitude], {
      icon: createDayBadge(day, color), interactive: false, zIndexOffset: 750,
    }));
  }

  private addJourneyDayBadge(segment: JourneyDaySegment, stops: City[], active: ActiveStops): void {
    const point = segment.coordinates?.[0];
    if (!point) return;
    const station = stops.find(stop => stop.id === segment.stationIds[0]);
    if (point[0] === station?.latitude && point[1] === station.longitude && isActiveStop(station, active)) return;
    this.groups.main.addLayer(L.marker(point, {
      icon: createDayBadge(segment.day, segment.color), interactive: false, zIndexOffset: 750,
    }));
  }

  private renderLegDayBadges(leg: TripLeg, stops: City[], active: ActiveStops, seenDays: Set<number>): void {
    if (!leg.dayNumber) return;
    const fallback = stops.filter(stop => stop.id === leg.fromStationId || stop.id === leg.toStationId);
    const stationData = leg.stationData?.length ? leg.stationData : fallback;
    for (const segment of journeyDaySegments({ ...leg, stationData }, leg.dayNumber, leg.dayColor)) {
      if (seenDays.has(segment.day)) continue;
      if (segment.day > 1) this.addJourneyDayBadge(segment, stops, active);
      seenDays.add(segment.day);
    }
  }

  renderTripDayBadges(
    tripStops: City[], tripLegs: TripLeg[],
    tripStationDayMap: Map<string, { color: string; day: number }> | undefined,
    activeStationIds: Set<string>, activeCityNames: Set<string>, activeRoute?: TrainRoute,
  ): void {
    const active = { ids: activeStationIds, cities: activeCityNames };
    const activeLeg = tripLegs.find(leg => leg.routeId === activeRoute?.id && !leg.requiresSelection);
    const previewDays = activeRoute && activeLeg ? journeyDaySegments(activeRoute, activeLeg.dayNumber).map(segment => segment.day) : [];
    const seenDays = new Set(previewDays);
    for (const leg of tripLegs) {
      if (leg.requiresSelection || leg.routeId !== activeRoute?.id) this.renderLegDayBadges(leg, tripStops, active, seenDays);
    }
    if (seenDays.size > 0 || !tripStationDayMap?.size) return;
    for (const [id, info] of tripStationDayMap) {
      if (info.day <= 1 || seenDays.has(info.day)) continue;
      seenDays.add(info.day);
      this.addTripDayBadge(tripStops.find(station => station.id === id), info.day, info.color, active);
    }
  }

  // ── 3. Trip stop markers ──────────────────────────────────────────

  renderTripStops(
    tripStops: City[],
    tripLegs: TripLeg[],
    tripStationDayMap: Map<string, { color: string; day: number }> | undefined,
    stopActions: StopActions,
    selectedStop: City | null,
    active: ActiveStops,
    cb: MarkerCallbacks
  ): void {
    if (tripStops.length < 2) return;
    const layers = this.groups.main;
    const map = this.map;

    const hasMultipleDays = tripStationDayMap
      ? new Set([...tripStationDayMap.values()].map((v) => v.day)).size > 1
      : false;

    const stationTimeLookup = savedStationTimes(tripLegs, tripStationDayMap);

    tripStops.forEach((station, idx) => {
      if (isActiveStop(station, active)) return;

      const isSelected = selectedStop?.id === station.id;
      const isEnd = idx === 0 || idx === tripStops.length - 1;
      const dayInfo = tripStationDayMap?.get(station.id);
      const markerColor = dayInfo?.color || DAY_COLORS[0];
      const actions = stopActions[station.id];
      const hasActions = actions && (actions.food || actions.overnight || actions.fika);
      const times = stationTimeLookup.get(station.id);
      const displayTime = times?.arrival || times?.departure || "";

      if (hasActions) {
        const actionsObj = { food: !!actions.food, overnight: !!actions.overnight, fika: !!actions.fika };
        const dayColor = dayInfo?.color || DAY_COLORS[0];

        const marker = L.marker([station.latitude, station.longitude], {
          icon: createPinnedStopMarker({ cityName: station.city, timeLabel: pinnedTimeLabel(times, actions.overnight, hasMultipleDays), dayColor, actions: actionsObj, isSelected }),
          zIndexOffset: isSelected ? 900 : 200,
        });
        bindStopClick(marker, station, map, cb);
        layers.addLayer(marker);
        return;
      }

      const restingIndex = isEnd ? 500 : 50;
      const marker = L.marker([station.latitude, station.longitude], {
        icon: createRouteMarker(markerColor, isEnd, station.poiType, isSelected, false, false),
        zIndexOffset: isSelected ? 900 : restingIndex,
      });

      const label = tripStopLabel(idx, tripStops.length);
      const poiSvg = station.poiType ? iconElement(station.poiType, "svg", true) : "";
      const poiSpan = poiSvg ? `<span style="margin-left:4px;vertical-align:middle;">${poiSvg}</span>` : "";
      const dayLabel = hasMultipleDays && dayInfo
        ? `<span style="display:block;font-size:10px;color:${dayInfo.color};font-weight:600">Day ${dayInfo.day}</span>` : "";
      const timeLbl = displayTime
        ? `<span style="display:block;font-size:10px;color:${NEUTRAL.secondary};font-weight:500">${escapeHtml(displayTime)}</span>` : "";

      // IMPORTANT — Intentional design: map markers show the clean CITY name
      // (station.city), while the card (StopDiscoveryCard) shows the full STATION
      // name (station.name) once the user clicks. Do NOT change this.
      marker.bindTooltip(
        `<div style="font-size:12px;line-height:1.3">
          <span style="font-weight:600">${escapeHtml(station.city)}</span>${poiSpan}
          ${dayLabel}${timeLbl}
          <span style="display:block;font-size:10px;color:${NEUTRAL.tertiary};font-weight:500">${label}</span>
        </div>`,
        { direction: "top", offset: [0, -10] }
      );
      bindStopClick(marker, station, map, cb);
      layers.addLayer(marker);
    });
  }

  // ── 3b. Intermediate station markers along trip legs ──────────────
  // Renders the small station dots for every intermediate stop that the
  // train passes through on each leg — the same dots that appear on an
  // active search route, but for already-saved/planned trip legs.

  renderTripLegStations(
    tripStops: City[],
    tripLegs: TripLeg[],
    tripStationDayMap: Map<string, { color: string; day: number }> | undefined,
    activeStationIds: Set<string>,
    activeCityNames: Set<string>,
    cb: MarkerCallbacks
  ): void {
    if (tripLegs.length === 0) return;
    const map = this.map;
    const layers = this.groups.main;
    const hiddenStationIds = new Set([...tripStops.map(station => station.id), ...activeStationIds]);

    const hasMultipleDays = tripStationDayMap
      ? new Set([...tripStationDayMap.values()].map((v) => v.day)).size > 1
      : false;

    for (const leg of tripLegs) {
      // Skip ghost legs that are still loading or errored — they have no real station data
      if (leg.ghostLoading || leg.ghostError || !leg.stationData?.length) continue;

      const startDay = leg.dayNumber ?? tripStationDayMap?.get(leg.fromStationId)?.day ?? 1;

      // Build a time lookup from the leg's stationData
      const timeLookup = new Map<string, { arrival?: string; departure?: string }>();
      for (const sd of leg.stationData) {
        timeLookup.set(sd.id, { arrival: sd.arrival, departure: sd.departure });
      }

      for (const sd of leg.stationData) {
        // Skip endpoints (they are rendered by renderTripStops) and trip stops
        if (hiddenStationIds.has(sd.id)) continue;

        const station = getCityById(sd.id) ?? sd;
        if (!hasCoordinates(station) || activeCityNames.has(station.city.toLowerCase())) continue;
        const { day: dayNum, color } = tripStationPresentation(leg, sd, startDay);

        const marker = L.marker([station.latitude, station.longitude], {
          icon: createRouteMarker(color, false, station.poiType, false, false, false),
          zIndexOffset: 50,
        });

        const times = timeLookup.get(sd.id);
        const displayTime = times?.arrival || times?.departure || "";
        const tooltip = stationTooltip(station, displayTime, color, dayNum, hasMultipleDays || dayNum > 1);

        marker.bindTooltip(tooltip, { direction: "top", offset: [0, -10] });
        bindStopClick(marker, station, map, cb);
        layers.addLayer(marker);
      }
    }
  }

  // ── 4. Inactive search-route polylines ────────────────────────────

  renderInactiveRoutes(routes: TrainRoute[], activeRouteIndex: number, cb: MarkerCallbacks): void {
    const geometry = (route: TrainRoute) => route.routeCoords ?? resolveCoords(route.stations);
    const edgeKey = (a: [number, number], b: [number, number]) =>
      [a.map(n => n.toFixed(5)).join(","), b.map(n => n.toFixed(5)).join(",")].sort((left, right) => left.localeCompare(right)).join("→");
    const seen = new Set<string>();
    const selected = routes[activeRouteIndex];
    const activeCoords = selected ? geometry(selected) : [];
    for (let i = 1; i < activeCoords.length; i++) seen.add(edgeKey(activeCoords[i - 1], activeCoords[i]));
    routes.forEach((route, index) => {
      if (index === activeRouteIndex) return;
      const coords = geometry(route);
      const segments: [number, number][][] = [];
      let segment: [number, number][] = [];
      for (let i = 1; i < coords.length; i++) {
        const key = edgeKey(coords[i - 1], coords[i]);
        if (seen.has(key)) {
          if (segment.length > 1) segments.push(segment);
          segment = [];
          continue;
        }
        seen.add(key);
        if (!segment.length) segment.push(coords[i - 1]);
        segment.push(coords[i]);
      }
      if (segment.length > 1) segments.push(segment);
      for (const points of segments) {
        const polyline = this.addRoutePolyline(points, {
          color: "#9ca3af", weight: 2, opacity: 0.3, dashArray: "10 10",
        });
        polyline.bindTooltip(
          `<span style="font-size:12px;font-weight:500">${escapeHtml(route.name)} · ${escapeHtml(route.departureTime)} → ${escapeHtml(route.arrivalTime)} · Alternative route — click to select</span>`,
          { sticky: true }
        );
        polyline.on("click", (event: L.LeafletMouseEvent) => { L.DomEvent.stopPropagation(event); cb.onSelectRoute(index); });
        polyline.on("mouseover", (e: L.LeafletMouseEvent) => { (e.target as L.Polyline).setStyle({ weight: 4, opacity: 0.6 }); });
        polyline.on("mouseout", (e: L.LeafletMouseEvent) => { (e.target as L.Polyline).setStyle({ weight: 2, opacity: 0.3 }); });
      }
    });
  }

  // ── 5. Active search-route polylines + markers ────────────────────

  renderActiveRoute(
    activeRoute: TrainRoute,
    daySegments: JourneyDaySegment[],
    stopActions: StopActions,
    selectedStop: City | null,
    getDayColor: (id: string) => string,
    getDayNumber: (id: string) => number,
    cb: MarkerCallbacks
  ): void {
    const map = this.map;
    const layers = this.groups.main;

    const snapshot = new Map(activeRoute.stationData?.map(s => [s.id, s]));
    const segmentCoords = (ids: string[]): [number, number][] => {
      if (daySegments.length === 1 && activeRoute.routeCoords?.length) return activeRoute.routeCoords;
      return ids.flatMap(id => {
        const station = snapshot.get(id) ?? getCityById(id);
        return station ? [[station.latitude, station.longitude] as [number, number]] : [];
      });
    };
    for (let i = 0; i < daySegments.length; i++) {
      const seg = daySegments[i];
      // Join into the next day's first stop without duplicating timeline entries.
      const boundary = daySegments[i + 1]?.stationIds[0];
      const ids = boundary ? [...seg.stationIds, boundary] : seg.stationIds;
      const coords = seg.coordinates ?? segmentCoords(ids);
      if (coords.length < 2) continue;

      const polyline = this.addRoutePolyline(coords, {
        color: seg.color, weight: 4, opacity: 0.9, dashArray: "10 8", lineCap: "round", lineJoin: "round",
      });

      const fromSt = getCityById(activeRoute.from);
      const toSt = getCityById(activeRoute.to);
      const fromCity = fromSt?.city || "";
      const toCity = toSt?.city || "";

      polyline.on("click", (e: L.LeafletMouseEvent) => {
        L.DomEvent.stopPropagation(e);
        const popupContent = document.createElement("div");
        popupContent.style.cssText = `font-family:${FONT_STACK};min-width:180px;`;

        const header = document.createElement("div");
        header.style.cssText = `font-size:13px;font-weight:600;color:${NEUTRAL.title};margin-bottom:4px;`;
        header.textContent = `${fromCity} \u2192 ${toCity}`;

        const times = document.createElement("div");
        times.style.cssText = "font-size:11px;color:#6b7280;margin-bottom:2px;";
        times.textContent = `${activeRoute.departureTime} \u2192 ${activeRoute.arrivalTime} \u00b7 ${activeRoute.duration}`;

        const operator = document.createElement("div");
        operator.style.cssText = "font-size:10px;color:#9ca3af;margin-bottom:8px;";
        operator.textContent = activeRoute.operator;

        popupContent.appendChild(header);
        popupContent.appendChild(times);
        popupContent.appendChild(operator);

        const btn = document.createElement("button");
        btn.textContent = "Use this train";
        btn.style.cssText = `width:100%;min-height:44px;padding:6px 12px;background:#6366f1;color:white;border:none;border-radius:6px;font-size:12px;font-weight:600;cursor:pointer;font-family:${FONT_STACK};`;
        btn.onmouseenter = () => { btn.style.background = "#4f46e5"; };
        btn.onmouseleave = () => { btn.style.background = "#6366f1"; };
        btn.onclick = () => { cb.onAddRouteToTrip?.(activeRoute); map.closePopup(); };
        popupContent.appendChild(btn);

        L.popup({ closeButton: true, className: "" })
          .setLatLng(e.latlng)
          .setContent(popupContent)
          .openOn(map);
      });

    }

    // Station markers
    const stationList = activeRoute.stations
      .map((id) => snapshot.get(id) ?? getCityById(id))
      .filter(hasCoordinates);

    const routeStationTimes = new Map<string, { arrival: string | undefined; departure: string | undefined }>();
    if (activeRoute.stationData) {
      for (const s of activeRoute.stationData) {
        routeStationTimes.set(s.id, { arrival: s.arrival, departure: s.departure });
      }
    }

    stationList.forEach((station, idx) => {
      const isEndpoint = idx === 0 || idx === stationList.length - 1;
      const isSelected = selectedStop?.id === station.id;
      const actions = stopActions[station.id];
      const color = getDayColor(station.id);
      const hasActions = actions && (actions.food || actions.overnight || actions.fika);

      if (hasActions) {
        const stTimes = routeStationTimes.get(station.id);
        const multiDay = daySegments.length > 1 || getDayNumber(station.id) > 1;
        const dayNum = getDayNumber(station.id);
        const actionsObj = { food: !!actions.food, overnight: !!actions.overnight, fika: !!actions.fika };
        const departureDay = journeyStationDay(activeRoute, snapshot.get(station.id) ?? station, daySegments[0]?.day ?? 1, true);
        const labelTime = pinnedTimeLabel({ ...stTimes, arrivalDay: dayNum, departureDay }, actions.overnight, multiDay);

        const marker = L.marker([station.latitude, station.longitude], {
          icon: createPinnedStopMarker({ cityName: station.city, timeLabel: labelTime, dayColor: color, actions: actionsObj, isSelected }),
          zIndexOffset: isSelected ? 1000 : 300,
        });
        bindStopClick(marker, station, map, cb);
        layers.addLayer(marker);
        return;
      }

      const restingIndex = isEndpoint ? 500 : 100;
      const marker = L.marker([station.latitude, station.longitude], {
        icon: createRouteMarker(color, isEndpoint, station.poiType, isSelected, !!actions?.food, !!actions?.overnight),
        zIndexOffset: isSelected ? 1000 : restingIndex,
      });

      const stTimes = routeStationTimes.get(station.id);
      const stDisplay = stTimes?.arrival || stTimes?.departure || "";
      const tooltip = stationTooltip(station, stDisplay, getDayColor(station.id), getDayNumber(station.id), daySegments.length > 1 || getDayNumber(station.id) > 1);

      marker.bindTooltip(tooltip, { direction: "top", offset: [0, -10], permanent: isEndpoint, className: "" });
      bindStopClick(marker, station, map, cb);
      layers.addLayer(marker);
    });

    // Day badges at overnight boundaries
    for (const seg of daySegments.filter(segment => segment.day > 1)) {
        const station = snapshot.get(seg.stationIds[0]) ?? getCityById(seg.stationIds[0]);
        const point = seg.coordinates?.[0] ?? (station ? [station.latitude, station.longitude] as [number, number] : undefined);
        if (!point) continue;
        layers.addLayer(L.marker(point, {
          icon: createDayBadge(seg.day, seg.color),
          interactive: false,
          zIndexOffset: 800,
        }));
    }
  }

  // ── 6. POI dot markers ────────────────────────────────────────────

  renderPOIs(
    viewMode: "search" | "explore",
    allPOIs: POI[] | undefined,
    routePOIs: POI[] | undefined,
    tripPOIs: POI[] | undefined,
    exploreFilters: ExploreFilters | undefined,
    cb: MarkerCallbacks
  ): void {
    const poiLayer = this.groups.poi;
    poiLayer.clearLayers();
    const map = this.map;

    const poisToRender = selectMapPOIs(viewMode, allPOIs, routePOIs, tripPOIs, exploreFilters);

    if (poisToRender.length === 0) return;

    const symbolMap = getPoiSymbolMap();

    for (const poi of poisToRender) {
      const cfg = POI_CONFIG[poi.poiType as POIType];
      const sym = symbolMap[poi.poiType as POIType] ?? cfg.symbol;
      const icon = createPOIDot(cfg.color, sym);

      const marker = L.marker([poi.latitude, poi.longitude], { icon, zIndexOffset: 200 });

      marker.on("mouseover", () => {
        if (cb.onSetHoveredPOI) {
          const pt = map.latLngToContainerPoint([poi.latitude, poi.longitude]);
          cb.onSetHoveredPOI(poi, { x: pt.x, y: pt.y });
        }
      });
      marker.on("mouseout", () => { cb.onClearHoveredPOI?.(); });

      marker.on("click", () => {
        cb.onClearHoveredPOI?.();
        // POI is already a City — just override id with poi- prefix for trip stop identity
        const cityFromPOI: City = {
          ...poi,
          id: `poi-${poi.id}`,
          name: poi.poiName || poi.city,
        };
        if (cb.onSelectStopWithPosition) {
          const pos = map.latLngToContainerPoint([poi.latitude, poi.longitude]);
          cb.onSelectStopWithPosition(cityFromPOI, { x: pos.x, y: pos.y });
        } else {
          cb.onSelectStop(cityFromPOI);
        }
      });

      poiLayer.addLayer(marker);
    }
  }

  // ── Lifecycle ────────────────────────────────────────────────────

  destroy(): void {
    for (const g of Object.values(this.groups)) {
      g.clearLayers();
      this.map.removeLayer(g);
    }
  }
}
